import ServiceLayout from '@/pages/services/components/ServiceLayout';
import type { ServiceContent } from '@/pages/services/components/ServiceLayout';

export const content: ServiceContent = {
  tag: 'AI Operation',
  title: '導入したAIが、現場で使われていない。',
  subtitle: 'AIは、使い始めてからが本番です。',
  description:
    '使い方が定着しない。利用する人が増えない。導入後の改善が止まっている。LUVIRAは、AIを業務で使い続けるための運用と改善を支援します。',
  icon: 'ri-settings-5-line',
  heroColor: {
    blob1: 'linear-gradient(135deg, #059669, #14B8A6)',
    blob2: 'linear-gradient(135deg, #10B981, #0D9488)',
    accent: '#34D399',
    glow: 'from-emerald-400 to-teal-400',
  },
  aboutTitle: 'AI運用・改善支援とは',
  aboutParagraphs: [
    '導入したAIを業務で使い続けるために、利用状況や現場の課題を確認し、運用方法を見直していくサービスです。',
    'LUVIRAは、現場での利用状況を確認し、必要な運用方法や改善内容を検討します。',
  ],
  challenges: [
    'AIを導入したが、利用する人が限られている。',
    '現場がどのように使えばよいか分かっていない。',
    '使い始めたものの、期待した効果が出ていない。',
    'AIの回答や出力内容にばらつきがある。',
    '利用ルールや確認方法が決まっていない。',
    '担当者が変わると、AIの活用が止まりそうである。',
    '導入後に何を改善すればよいか分からない。',
    'AIを一度導入して終わりにしたくない。',
  ],
  featuresTitle: 'AI運用・改善でできること',
  features: [
    {
      icon: 'ri-radar-line',
      title: '利用状況を確認する',
      items: ['AIが現場でどのように使われているかを把握します。'],
    },
    {
      icon: 'ri-file-list-3-line',
      title: '使い方を整える',
      items: ['業務に合った使い方を整理し、現場に共有します。'],
    },
    {
      icon: 'ri-tools-line',
      title: '出力内容を改善する',
      items: ['回答や出力のばらつきを確認し、改善します。'],
    },
    {
      icon: 'ri-shield-check-line',
      title: '運用ルールを整える',
      items: ['利用ルールや確認方法を整え、運用に落とし込みます。'],
    },
    {
      icon: 'ri-refresh-line',
      title: '改善を続ける',
      items: ['導入後の課題を次の改善につなげます。'],
    },
  ],
  valueTitle: 'AIが業務で使われ続けることで得られる価値',
  value: [
    'AIを使う業務と使わない業務を分けやすくなる。',
    '利用者がAIの使い方を確認しやすくなる。',
    'AIの出力内容を業務で活用しやすくなる。',
    '担当者が変わっても運用を続けやすくなる。',
    '導入後の課題を次の改善につなげやすくなる。',
  ],
  whyTitle: 'なぜLuviraなのか',
  why: [
    'LUVIRAは、AIを導入して終わりにはしません。導入後の利用状況を確認します。現場が感じている課題を聞き取ります。業務に合った使い方を検討します。AIと人の役割分担を見直します。AIを使い続けるための運用方法を一緒に考えます。',
  ],
  casesTitle: '支援事例',
  casesDescription: '現在、掲載準備中です。',
  flowTitle: '支援までの流れ',
  flow: [
    { step: 'STEP 1', title: '現在の利用状況を確認する', items: ['AIの利用状況と現場の実態を把握します。'] },
    { step: 'STEP 2', title: '運用上の課題を整理する', items: ['使われない理由や改善点を整理します。'] },
    { step: 'STEP 3', title: '改善内容を決める', items: ['取り組む改善内容と優先順位を定めます。'] },
    { step: 'STEP 4', title: '運用方法を見直す', items: ['使い方やルールを見直し、運用します。'] },
    { step: 'STEP 5', title: '継続的に改善する', items: ['利用状況を確認し、改善を続けます。'] },
  ],
  faq: [
    {
      q: 'AIを導入した後からでも相談できますか？',
      a: 'はい。すでに導入しているAIやツールの利用状況を確認し、運用上の課題や改善方法を一緒に考えます。',
    },
    {
      q: 'AIを導入したものの、ほとんど使われていません。',
      a: '使われていない理由を確認します。業務との接続、使い方、情報、ルールなどを確認し、改善できる部分を検討します。',
    },
    {
      q: 'AIの回答や出力内容にばらつきがあります。',
      a: '利用方法、指示内容、参照する情報、確認工程を確認し、業務で使いやすい状態にする方法を検討します。',
    },
    {
      q: '社内にAIの担当者がいなくても大丈夫ですか？',
      a: 'はい。現在の体制と運用状況を確認し、必要な役割や進め方を一緒に考えます。',
    },
    {
      q: '利用ルールだけを相談できますか？',
      a: 'はい。利用する情報、入力してはいけない情報、確認方法、利用者の範囲など、必要なルールを検討します。',
    },
    {
      q: '継続的な支援が必要ですか？',
      a: '課題や運用状況によって異なります。必要な支援範囲と頻度を確認したうえで、進め方を検討します。',
    },
    {
      q: 'AIシステムの改修や開発もできますか？',
      a: '現在のシステムや業務環境を確認したうえで、改修や開発が必要かどうかを検討します。',
    },
  ],
  cta: {
    title: 'AIを、使い続けられる業務へ。',
    description:
      'AIを導入したものの、現場で使われていない。使い方やルールが決まらず、改善が止まっている。その状態からご相談いただけます。LUVIRAが利用状況と業務上の課題を確認し、AIを使い続けるための運用方法を一緒に考えます。',
    buttonText: 'AI運用・改善について相談する',
    serviceKey: 'AI運用・改善支援',
  },
};

export default function AiOperationPage() {
  return <ServiceLayout content={content} />;
}